import { Events } from 'backbone';
import _ from 'underscore';
import { v4 as UUID } from 'uuid';
import Api from './api';
import ArcadeConfig, { ATTRIBUTES as ARCADE_ATTRIBUTES } from './data/arcadeConfig';
import Environment from './environment';
import { getLogger } from './logging/logger';

/**
 * @typedef {object} UserAnswer
 * @property {any} userAnswer The answer index, score, or some other value represetning
 * 		the users input to a game round or question.
 * @property {string} engine The game engine that this answer data is associated with
 * @property {number} [timesAnswered] The number of times a question/round has been answered
 * 		by the user
 */

const logger = getLogger('HwpTvApi');

function deepMerge(...args) {
	logger.log('[deepMerge]');

	const dest = {};
	args.forEach((src) => {
		Object.keys(src).forEach((key) => {
			if (_.isObject(src[key])) {
				dest[key] = deepMerge(dest[key] || {}, src[key]);
			} else if (_.isArray(src[key])) {
				dest[key] = Array.from(src[key]);
			} else {
				dest[key] = src[key];
			}
		});
	});
	return dest;
}

function assertValuePromise(value, name) {
	if (!value) {
		const error = new Error(`Invalid parameter "${name}" with value "${value}"`);
		logger.error(error);
		return Promise.reject(error);
	}
	return Promise.resolve();
}

class HwpTvApi extends Api {
	constructor() {
		logger.log('[initialize]');

		super();

		Object.assign(this, Events);

		this.headers = {
			session: UUID(),
		};
		this.baseUrl = Environment.isProd()
			? 'https://api.tv.hollywoodplayer.com/v2'
			: 'https://api.devtv.hollywoodplayer.com';

		this.checkIsHub();

		// Listen just in case we checked too early
		ArcadeConfig.on(`change:${ARCADE_ATTRIBUTES.arcadeName}`, () => this.checkIsHub());
	}

	checkIsHub() {
		logger.log('[checkIsHub]');

		if (Environment.isHub() || Environment.isInHub()) {
			this.headers.isHub = true;
		}
	}

	/**
        UTILITY AND MISC ENDPOINTS
    */
	greet() {
		logger.log('[greet]');
		return this.get('/');
	}

	greetPost(data) {
		logger.log('[greetPost]');
		return this.post('/', data);
	}

	echo() {
		logger.log('[echo]');
		return this.get('/echo/');
	}

	/**
        GAME ENDPOINTS
    */
	listGames() {
		return this.get('/game');
	}

	getGame(gameId) {
		return this.get(`/game/${gameId}`);
	}

	/**
        GAME SCORE ENDPOINTS
    */
	getGameTopScores(gameId) {
		assertValuePromise(gameId, 'gameId').then(() => this.get(`/game/${gameId}/score`));
	}

	getGameScoresForUser(gameId) {
		return assertValuePromise(gameId, 'gameId').then(() =>
			this.get(`/game/${gameId}/score/user`),
		);
	}

	addGameScore(gameId, score, engine, possible) {
		return assertValuePromise(gameId, 'gameId').then(() => {
			let url = `/game/${gameId}/score/${score}`;
			if (possible !== undefined) {
				url += `/${possible}`;
			}
			return this.post(url, { gameId, score, possible, engine });
		});
	}

	getGameRankBoard(gameId, sort = '') {
		return assertValuePromise(gameId, 'gameId').then(() =>
			this.get(`/game/${gameId}/rankBoard/${sort}`),
		);
	}

	getGameUserRank(gameId, sort = '') {
		return assertValuePromise(gameId, 'gameId').then(() =>
			this.get(`/game/${gameId}/user/rank/${sort}`),
		);
	}

	/**
		GAME USER ANSWER ENDPOINTS
	*/

	/**
	 * Saves a users anser for a give game/question combo to the server for resuming a game
	 * based on the users progress or some other functionality.
	 * @param {string} gameId The gameId for the users answer
	 * @param {string} questionId The questionId for a users answer
	 * @param {UserAnswer} data Additional data to store for the user answer
	 * @returns {Promise<any>} Resolves when user answer is stored server side.
	 */
	submitGameUserAnswer(gameId, questionId, data) {
		logger.log('[submitGameUserAnswer]', gameId, questionId, data);

		return assertValuePromise(gameId, 'gameId').then(() =>
			this.post(`/game/${gameId}/user/answer/${questionId}`, { gameId, questionId, ...data }),
		);
	}

	/**
	 * Fetches a users answer
	 * @param {string} gameId The gameId to fetch a user answer for
	 * @param {string} [questionId = ''] The question/round id to fetch the user answer for. If
	 * 		not provided all users answers for a game will be fetched.
	 * @returns {Promise<UserAnswer>|Promise<{ data:UserAnswer[] }>}
	 */
	getGameUserAnswer(gameId, questionId = '') {
		logger.log('[getGameUserAnswer]', gameId, questionId);

		return assertValuePromise(gameId, 'gameId').then(() =>
			this.get(`/game/${gameId}/user/answer/${questionId}`),
		);
	}

	getGameAnswerCounts(gameId, questionId) {
		return this.get(`/game/${gameId}/${questionId}/counts`);
	}

	getGameTotalPoints(gameId) {
		return this.get(`/game/${gameId}/totalPoints`);
	}

	/**
        USER ENDPOINTS
        const DEFAULT_VALUES = {
            id: undefined,
            stbId: undefined,
            firstName: undefined,
            lastName: undefined,
            email: undefined,
            created: undefined,
            lastUpdate: undefined,
            state: 'uninitialized'
        };
    */
	getUserUnreadMessages() {
		return this.get(`/user/messages`).then(({ messages = [] }) => messages);
	}

	setUserMessageAsRead(messageId) {
		return this.post(`/user/messages/${messageId}`);
	}

	getUser(userId) {
		userId = userId || '';
		return this.get(`/user/${userId}`);
	}

	getUserStats() {
		return this.get(`/user/stats`);
	}

	getUserAchievements(userId = '') {
		return this.get(`/user/${userId ? `${userId}/` : ''}achievements`).then((res) =>
			res.achievements.map((achievement) => {
				// Convert isOn to boolean values since that's what view's expect right now
				if (achievement.isOn === 'true') {
					achievement.isOn = true;
				} else if (achievement.isOn === 'false') {
					achievement.isOn = false;
				}
				return achievement;
			}),
		);
	}

	/**
	 * Sets a specific property for a user's achievement to an updated status
	 * @param {string} achievmentId Id of the achievement to update
	 * @param {string} property The Propety of the achievement to update
	 * @param {string} status The state to update the achievement property to
	 */
	setUserAchievementProperty(achievmentId = '', property = '', status = '') {
		return this.post(`/user/achievements/${achievmentId}/${property}/${status}`);
	}

	getFeaturedAchievements() {
		return this.get(`/achievements/featured`).then(
			({ featuredAchievements }) => featuredAchievements,
		);
	}

	getUserByStbId(stbId) {
		return this.get(`/user/stbId/${stbId}`);
	}

	putUser(data) {
		return this.post(`/user/`, data);
	}

	resetUser() {
		return this.get(`/user/reset/`);
	}

	authorize(options) {
		return this.get(`/auth`, options);
	}

	/**
        SWEEPSTAKES ENDPOINTS
    */
	putSweepstakes(sweepstakesData) {
		return this.post('/sweepstakes', sweepstakesData);
	}

	listSweepstakes(state) {
		return this.get(`/sweepstakes/state/${state}`);
	}

	getSweepstakes(sweepstakesId) {
		return this.get(`/sweepstakes/${sweepstakesId}`);
	}

	enterSweepstakes(sweepstakesId) {
		return this.get(`/sweepstakes/${sweepstakesId}/enter`);
	}

	getSweepstakesEntries(sweepstakesId) {
		return this.get(`/sweepstakes/${sweepstakesId}/entries`);
	}

	/**
	 * RAFFLE ENDPOINTS
	 */
	getRaffle(client, raffleId) {
		return this.get(`/raffle/${client}/${raffleId}`);
	}

	enterRaffle(client, raffleId, entryData) {
		return this.post(`/raffle/${client}/${raffleId}/enter`, entryData);
	}

	/**
        Short Codes
    */
	createShortCode(value) {
		return this.post(`/code/`, value).then(({ code }) => code);
	}

	resolveShortCode(code) {
		return this.get(`/code/${code}`);
	}

	/**
	 * Sends application events to be logged server side
	 *
	 * @param {string} eventSource The name of the arcade
	 * @param {string} action The name of the event that happened
	 * @param {object} eventData Additional data for the event
	 * @returns {Promise}
	 */
	logEvent(eventSource, action, eventData) {
		logger.log('[logEvent]', { eventSource, action, eventData });

		return this.post(`/analytics/`, {
			eventSource,
			action,
			clientTime: Date.now(),
			...eventData,
		});
	}

	/**
        REQUEST METHODS
    */
	get(path, options = {}) {
		logger.log('[get]', { path, options });

		options = deepMerge({ headers: this.headers }, options);

		return this.send('GET', `${this.baseUrl}${path}?t=${Date.now()}`, null, options);
	}

	post(path, data, options = {}) {
		logger.log('[post]', { path, data, options });

		options = deepMerge({ headers: this.headers }, options);

		return this.send('POST', `${this.baseUrl}${path}?t=${Date.now()}`, data, options).then(
			(response) => {
				this.trigger('posted');
				return Promise.resolve(response);
			},
			(error) => {
				logger.error(error);
				this.trigger('error', error);
				return Promise.reject(error);
			},
		);
	}
}

export default new HwpTvApi();
